import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Crown, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [plan, setPlan] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const u = await refresh();
          setPlan(u?.plan);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="min-h-screen bg-ho-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
        <Logo />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="mt-8 w-full rounded-[32px] border border-black/5 bg-white p-8">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-ho-primary" />
              <h1 className="font-display mt-5 text-2xl font-bold">Confirming your payment…</h1>
              <p className="mt-2 text-ho-muted">Hang tight, this only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-emerald-100">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h1 className="font-display mt-5 text-2xl font-bold">You're all set! 🎉</h1>
              <p className="mt-2 text-ho-muted">
                Your <span className="font-semibold capitalize text-ho-ink">{plan || "Pro"}</span> plan is active. Publish as many sites as you like.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-ho-primary/10 px-4 py-2 text-sm font-semibold text-ho-primary">
                <Crown className="h-4 w-4" /> {plan || "Pro"} unlocked
              </div>
              <button onClick={() => navigate("/app")} data-testid="payment-success-continue"
                className="tap-scale mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ho-primary py-3.5 font-semibold text-white">
                Go to dashboard <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
          {(state === "timeout" || state === "error") && (
            <>
              <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-amber-100">
                <XCircle className="h-9 w-9 text-amber-600" />
              </div>
              <h1 className="font-display mt-5 text-2xl font-bold">
                {state === "timeout" ? "Still processing" : "Something went wrong"}
              </h1>
              <p className="mt-2 text-ho-muted">
                {state === "timeout"
                  ? "Your payment is taking longer than usual. Your plan will update automatically once it's confirmed."
                  : "We couldn't confirm this payment. If you were charged, it'll be reflected shortly."}
              </p>
              <button onClick={() => navigate("/app")} data-testid="payment-success-dashboard"
                className="tap-scale mt-6 w-full rounded-full bg-ho-ink py-3.5 font-semibold text-white">
                Back to dashboard
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
